import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { isNotNullOrUndefined } from '../../Util/TypeGuards';

export interface IOrganisationEntityAttributes extends IModelAttributes {
	name: string;
	shortName: string;

	configGeneralId?: string;
	configGeneral?: Models.ConfigGeneralEntity | Models.IConfigGeneralEntityAttributes;
	configThemeId?: string;
	configTheme?: Models.ConfigThemeEntity | Models.IConfigThemeEntityAttributes;
	routes: Array<Models.OrganisationRoute | Models.IOrganisationRouteAttributes>;
	tag: string;
	hasFerries: boolean;
	hasTours: boolean;
}
@entity('OrganisationEntity', 'Organisation')
export default class OrganisationEntity extends Model implements IOrganisationEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The name of the orgnisation.
	 */
	@observable
	@attribute<OrganisationEntity, string>()
	public name: string;

	/**
	 * The short name of the organisation.
	 */
	@observable
	@attribute<OrganisationEntity, string>()
	public shortName: string;

	@observable
	@attribute()
	public configGeneralId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public configGeneral: Models.ConfigGeneralEntity;

	@observable
	@attribute()
	public configThemeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public configTheme: Models.ConfigThemeEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public routes: Models.OrganisationRoute[] = [];

	/**
	 * A tag to sort the organisation by.
	 */
	@observable
	@attribute<OrganisationEntity, string>()
	public tag: string;

	/**
	 * Whether or not the organisation has ferries
	 */
	@observable
	@attribute<OrganisationEntity, string>()
	public hasFerries: boolean;

	/**
	 * Whether or not the organisation has tours
	 */
	@observable
	@attribute<OrganisationEntity, string>()
	public hasTours: boolean;

	constructor(attributes?: Partial<IOrganisationEntityAttributes>) {
		super(attributes);

		this.routes = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IOrganisationEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.shortName !== undefined) {
				this.shortName = attributes.shortName;
			}
			if (attributes.configGeneralId !== undefined) {
				this.configGeneralId = attributes.configGeneralId;
			}
			if (attributes.configGeneral !== undefined) {
				if (attributes.configGeneral === null) {
					this.configGeneral = attributes.configGeneral;
				} else if (attributes.configGeneral instanceof Models.ConfigGeneralEntity) {
					this.configGeneral = attributes.configGeneral;
					this.configGeneralId = attributes.configGeneral.id;
				} else {
					this.configGeneral = new Models.ConfigGeneralEntity(attributes.configGeneral);
					this.configGeneralId = this.configGeneral.id;
				}
			}
			if (attributes.configThemeId !== undefined) {
				this.configThemeId = attributes.configThemeId;
			}
			if (attributes.configTheme !== undefined) {
				if (attributes.configTheme === null) {
					this.configTheme = attributes.configTheme;
				} else if (attributes.configTheme instanceof Models.ConfigThemeEntity) {
					this.configTheme = attributes.configTheme;
					this.configThemeId = attributes.configTheme.id;
				} else {
					this.configTheme = new Models.ConfigThemeEntity(attributes.configTheme);
					this.configThemeId = this.configTheme.id;
				}
			}
			if (attributes.routes !== undefined && Array.isArray(attributes.routes)) {
				if (!this.routes) {
					this.routes = [];
				}
				for (const model of attributes.routes) {
					if (model instanceof Models.OrganisationRoute) {
						this.routes.push(model);
					} else {
						this.routes.push(new Models.OrganisationRoute(model));
					}
				}
			}
			if (isNotNullOrUndefined(attributes.tag)) {
				this.tag = attributes.tag;
			}
			if (isNotNullOrUndefined(attributes.hasFerries)) {
				this.hasFerries = attributes.hasFerries;
			}
			if (isNotNullOrUndefined(attributes.hasTours)) {
				this.hasTours = attributes.hasTours;
			}
		}
	}
}
